import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

// Fixed bar width + gap so the visual density stays identical no matter how
// wide the input is — we simply render enough bars to fill the space and let
// overflow clip the rest.
const BAR_COUNT = 200;
const MIN_H = 3;
const MAX_H = 30;

/**
 * Minimal live waveform shown inside the chat box while the mic records.
 * Bars rest flat in silence and rise from the centre with real on-device
 * loudness (no audio leaves the browser until the user stops).
 * Updates bar heights directly via the DOM so it never re-renders React.
 * The overlay stays within the composer's own bounds — the box never grows.
 */
export function ListeningOverlay({ meter, className }: { meter: () => number; className?: string }) {
  const barsRef = useRef<Array<HTMLSpanElement | null>>([]);

  useEffect(() => {
    let raf = 0;
    const GATE = 0.04; // room hiss stays flat
    const GAIN = 2.6; // normal speaking voice reaches near full height
    const mid = (BAR_COUNT - 1) / 2;
    const shape = Array.from({ length: BAR_COUNT }, (_, i) => {
      const d = Math.abs(i - mid) / mid;
      return 0.55 + 0.45 * Math.cos((d * Math.PI) / 2);
    });
    const levels = new Array(BAR_COUNT).fill(0);
    let smooth = 0;
    const tick = () => {
      const raw = Math.min(1, Math.max(0, meter()));
      const gated = raw < GATE ? 0 : Math.min(1, Math.sqrt((raw - GATE) / (1 - GATE)) * GAIN * 0.5);
      smooth = gated > smooth ? gated * 0.7 + smooth * 0.3 : smooth * 0.82;
      barsRef.current.forEach((bar, i) => {
        if (!bar) return;
        const target = smooth > 0.02 ? Math.min(1, smooth * shape[i] * (0.55 + Math.random() * 0.6)) : 0;
        levels[i] = target > levels[i] ? target * 0.6 + levels[i] * 0.4 : levels[i] * 0.8;
        bar.style.height = `${Math.round(MIN_H + (MAX_H - MIN_H) * levels[i])}px`;
        bar.style.opacity = String(0.45 + 0.55 * Math.min(1, levels[i] * 2));
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [meter]);

  return (
    <div
      role="status"
      aria-label="Recording"
      className={cn(
        "pointer-events-none absolute inset-y-[3px] left-4 right-24 z-10 flex items-center justify-center gap-[4px] overflow-hidden bg-background px-1",
        className,
      )}
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        <span
          key={i}
          ref={(el) => { barsRef.current[i] = el; }}
          className="w-[2px] flex-none rounded-full bg-foreground"
          style={{ height: MIN_H, opacity: 0.45 }}
        />
      ))}
    </div>
  );
}
